import Head from 'next/head'
import DOMPurify from 'isomorphic-dompurify'
import { useContext, useState, useRef, useEffect } from 'react'
import PropTypes from 'prop-types'
import { AriaLive, getScreenSizes } from '@pretamanger/component-library'
import { commonPageProps, product } from '@proptypes'
import { useRouter } from 'next/router'
import useIntersection from 'react-use/lib/useIntersection'
import useMedia from 'react-use/lib/useMedia'
import { getMarketFromCtx } from '#lib/get-market'
import { OutOfStock } from '#src/common/components/out-of-stock'
import Translation from '#src/common/components/translation'
import { OrderAheadBagContext } from '#src/common/context/order-ahead-bag'
import Allergens from '../product-details-page/components/allergens'
import Disclaimer from '../product-details-page/components/disclaimer'
import Ingredients from '../product-details-page/components/ingredients'
import Nutritionals from '../product-details-page/components/nutritionals'
import ProductDietaryBadge from '../product-details-page/components/dietaryBadge'
import ProductImage from '../product-details-page/components/image'
import CoffeeWizardModal, {
  CoffeeWizardButton,
  isCoffeeWizardProduct
} from '#src/coffee-wizard'
import { getProductNonTransactional } from '../get-product-non-transactional'
import { getCumulativePrice, productUnAvailabilityRedirectPath } from './utils'
import { useProductDetails } from './use-product-details'
import { useGtmEvent } from './useGTMEvent'
import FoodCustomisationGroup from '../product-details-page/components/food-customisation-group'
import {
  AddToBagContainer,
  AddToBagCTA,
  ContentWrapper,
  ProductDescription,
  ProductDetails,
  ProductDetailsWrapper,
  ProductImageContainer,
  ProductTitle
} from './styles'
import ProductsLayoutWithLeftNav from '../components/layout/with-side-nav'
import {
  getAllCategoriesWithSubcategories,
  getCampaignMenuItems
} from '../get-all-categories'
import {
  hasMultiplePrices,
  hasFoodCustomisationGroups
} from '#src/products/util'
import { useRequireBag } from '../../common/hooks/useRequireBag'
import ProductCalories from '#src/common/components/product-calories'
import { getMappedSettings } from '#src/common/lib/get-site-settings'
import { getLaunchDarklyFlag } from '#api/flags'
import { anonymousIdHeader, outpostCutoffFlag } from '#constants'
import { parseCookies } from 'nookies'
import { fireEvent } from '#lib/events'
import { VIEW_ITEM } from '#lib/events/topics'

export const getServerSideProps = async ctx => {
  const { params, isTransactional = false, commonProps, locale } = ctx
  const market = getMarketFromCtx(ctx)

  const getProductPromise = async () =>
    isTransactional ? Promise.resolve(null) : getProductNonTransactional(ctx)
  const getAllCategoriesWithSubcategoriesPromise = async () =>
    getAllCategoriesWithSubcategories(ctx, market.id, isTransactional)

  const [product, categories] = await Promise.all([
    getProductPromise(),
    getAllCategoriesWithSubcategoriesPromise()
  ])

  const campaignMenus = !isTransactional
    ? getCampaignMenuItems(commonProps)
    : []

  const settings = getMappedSettings(locale)

  if (!isTransactional) {
    const redirectPath = productUnAvailabilityRedirectPath({
      product,
      locale,
      settings
    })
    if (redirectPath) {
      return redirectPath
    }
  }
  const cookies = parseCookies(ctx)
  const user = cookies[anonymousIdHeader]
  const cutoffTimeEnabledFlag = await getLaunchDarklyFlag(
    outpostCutoffFlag,
    user,
    market?.id
  )
  return {
    props: {
      ...commonProps,
      categories: campaignMenus.concat(categories),
      isTransactional,
      sku: params.sku,
      product,
      cutoffTimeEnabledFlag
    }
  }
}

const ProductDetailsPage = ({
  categories,
  settings,
  isTransactional,
  sku,
  product: _product
}) => {
  const { locale, query } = useRouter()
  useRequireBag(isTransactional)
  const {
    isLoading: bagLoading,
    addItems,
    addItemState
  } = useContext(OrderAheadBagContext)

  const intersectionRef = useRef(null)
  const intersection = useIntersection(intersectionRef, {
    root: null,
    threshold: 0.01
  })

  const {
    product,
    isLoading: isProductLoading,
    error
  } = useProductDetails({
    sku,
    product: _product,
    settings,
    isTransactional
  })

  if (isTransactional && error) {
    throw new Error(error)
  }
  const isMobileView = useMedia(`(max-width: ${getScreenSizes().md - 1}px)`)
  useGtmEvent({ isTransactional, product, categories })

  const {
    allergens,
    availableForCollection,
    description,
    image,
    ingredients,
    isOnStock,
    price,
    name,
    newUntil,
    nutritionalData = [],
    suitableForVegans,
    suitableForVegetarians,
    unavailableForOrderAhead
  } = product || {}

  const [isCoffeeWizardOpen, setCoffeeWizardOpen] = useState(false)
  const [customisationGroups, setCustomisationGroups] = useState([])

  const initCustomisationGroups = product => {
    return product?.customisationOptions.map(customisation => {
      return {
        ...customisation
      }
    })
  }

  useEffect(() => {
    const onSelectItemClick = product => {
      fireEvent(VIEW_ITEM, { product, category: query?.category })
    }
    if (product) {
      setCustomisationGroups(initCustomisationGroups(product))
    }
    onSelectItemClick(product)
  }, [product, query?.category])

  const handleAddToBag = async () => {
    const subLineItems = []
    const cumulativeOptions = customisationGroups.reduce((acc, curr) => {
      acc = [...acc, ...curr.options]
      return acc
    }, [])
    cumulativeOptions.forEach(option => {
      if (!option.isDefault && option.isSelected) {
        const { sku, name, price } = option
        subLineItems.push({
          sku,
          name,
          price,
          quantity: 1
        })
      }
    })

    const payload = {
      customisedItems: [
        {
          productSku: sku,
          productQuantity: 1,
          subLineItems
        }
      ]
    }
    await addItems({
      payload
    })
    setCustomisationGroups(initCustomisationGroups(product))
  }

  const onCustomisationsChange = updatedCustomisation => {
    const updatedGroups = customisationGroups.map(customisation => {
      if (customisation.type === updatedCustomisation.type) {
        return { ...updatedCustomisation }
      }
      return { ...customisation }
    })
    setCustomisationGroups(updatedGroups)
  }

  let addProductAlert = ''
  let isProductBeingAdded = false
  if (addItemState?.sku.includes(sku)) {
    isProductBeingAdded = addItemState.isAdding
    addProductAlert = isProductBeingAdded ? (
      <Translation id='product.activity.addingItemToBag' />
    ) : (
      <Translation id='product.activity.itemAddedToBag' tokens={{ name }} />
    )
  }

  const shouldCTABeSticky = !!intersection?.isIntersecting
  const ctaDisabled = bagLoading || unavailableForOrderAhead
  const displayCoffeeWizard = product ? isCoffeeWizardProduct(product) : false
  const visuallyHiddenName = <span className='sr-only'>{`: ${name}`}</span>

  const callToAction = () => {
    if (!isOnStock) {
      return (
        <OutOfStock>
          <Translation id='product.status.outOfStock' />
          {visuallyHiddenName}
        </OutOfStock>
      )
    }
    if (displayCoffeeWizard) {
      const _hasMultiplePrices = hasMultiplePrices(product)
      return (
        <CoffeeWizardButton
          disabled={ctaDisabled}
          styleType='primary'
          onClick={() => setCoffeeWizardOpen(true)}
        >
          {price && (
            <>
              <span aria-hidden> | </span>
              <span aria-hidden>
                {price.localisedPrice}
                {_hasMultiplePrices && '+'}
              </span>
              <span className='sr-only'>
                {_hasMultiplePrices ? (
                  <Translation
                    id='product.addToBag.cta.price.starting.from'
                    tokens={{ price: price.localisedPrice }}
                  />
                ) : (
                  price.localisedPrice
                )}
              </span>
              {visuallyHiddenName}
            </>
          )}
        </CoffeeWizardButton>
      )
    }
    if (unavailableForOrderAhead) {
      return (
        <AddToBagCTA
          data-testid='add-to-bag-pdp'
          styleType='quaternary'
          onClick={handleAddToBag}
          disabled
        >
          <Translation id='product.add-to-bag.disabled' />
        </AddToBagCTA>
      )
    }
    return (
      <>
        <AriaLive role='alert' className='sr-only'>
          {addProductAlert}
        </AriaLive>
        <AddToBagCTA
          activity={isProductBeingAdded}
          data-testid='add-to-bag-pdp'
          styleType='primary'
          onClick={handleAddToBag}
          disabled={ctaDisabled}
        >
          {!isProductBeingAdded && (
            <>
              <Translation id='product.pdp.add-to-bag.label' />
              <span aria-hidden> | </span>
              {visuallyHiddenName}
              <span>
                {getCumulativePrice(price, customisationGroups, locale)}
              </span>
            </>
          )}
        </AddToBagCTA>
      </>
    )
  }

  return (
    <>
      <Head>
        <meta name='description' content={description} />
      </Head>
      {isCoffeeWizardOpen && (
        <CoffeeWizardModal
          onClose={() => setCoffeeWizardOpen(false)}
          product={product}
        />
      )}
      <ProductsLayoutWithLeftNav
        categories={categories}
        selectedCategoryIndex={-1}
        isTransactional={isTransactional}
        isContentLoading={isProductLoading}
      >
        <ContentWrapper>
          {product && (
            <>
              <ProductTitle level='h1' styleOverride='headingMd'>
                {name}
              </ProductTitle>
              <ProductDetailsWrapper>
                <ProductImageContainer>
                  <ProductImage
                    image={image}
                    placeholder={settings.placeholderImage}
                    name={name}
                    newUntil={newUntil}
                  />
                </ProductImageContainer>
                <ProductDetails>
                  <ProductDescription
                    data-testid='product-description'
                    id='product-description'
                    dangerouslySetInnerHTML={{
                      __html: DOMPurify.sanitize(description)
                    }}
                  />
                  <ProductDietaryBadge
                    suitableForVegans={suitableForVegans}
                    suitableForVegetarians={suitableForVegetarians}
                  />
                  <ProductCalories
                    classes='font-medium mt-2'
                    nutritionalData={nutritionalData}
                  />
                  <div className='mt-4'>
                    <Allergens allergens={allergens} />
                  </div>
                  {!hasFoodCustomisationGroups(product) &&
                    isTransactional &&
                    price?.localisedPrice &&
                    availableForCollection && (
                      <AddToBagContainer isSticky>
                        {callToAction()}
                      </AddToBagContainer>
                    )}
                </ProductDetails>
              </ProductDetailsWrapper>
              {hasFoodCustomisationGroups(product) &&
                customisationGroups.map(customisationGroup => {
                  return (
                    <FoodCustomisationGroup
                      key={customisationGroup.type}
                      customisation={customisationGroup}
                      placeholderImage={settings.placeholderImage}
                      onCustomisationsChange={onCustomisationsChange}
                    />
                  )
                })}
              <div>
                {hasFoodCustomisationGroups(product) &&
                  isTransactional &&
                  price?.localisedPrice &&
                  availableForCollection &&
                  (isMobileView ? shouldCTABeSticky : true) && (
                    <AddToBagContainer
                      data-testid='add-to-bag-pdp-container'
                      isSticky={isMobileView ? shouldCTABeSticky : false}
                    >
                      {callToAction()}
                    </AddToBagContainer>
                  )}
              </div>
              <div ref={intersectionRef}>
                <Ingredients
                  ingredients={ingredients}
                  productName={name}
                  explicitTitle={hasFoodCustomisationGroups(product)}
                />
                <Disclaimer>
                  {displayCoffeeWizard && (
                    <Translation id='product.coffee.disclaimer' />
                  )}
                </Disclaimer>
                <Nutritionals product={product} />
              </div>
            </>
          )}
        </ContentWrapper>
      </ProductsLayoutWithLeftNav>
    </>
  )
}

ProductDetailsPage.propTypes = {
  ...commonPageProps,
  isTransactional: PropTypes.bool.isRequired,
  product,
  sku: PropTypes.string.isRequired
}

export default ProductDetailsPage
